import React, { useState } from 'react';
import { TariffDeterminationData, TariffAddon, TariffFlag } from 'types/chat.types';
import Icon from 'components/atoms/Icon';

interface TariffDeterminationResultProps {
  data: TariffDeterminationData;
  /** When true, render as a subordinate section inside another card (no outer
   *  card chrome, no hero total) with a one-line summary that expands details. */
  embedded?: boolean;
}

// Render order + labels for the additional-program addons.
const PROGRAM_ORDER = ['section_301', 'section_232', 'ieepa', 'section_122'] as const;
const PROGRAM_LABELS: Record<string, string> = {
  section_301: 'Section 301',
  section_232: 'Section 232',
  ieepa: 'IEEPA',
  section_122: 'Section 122',
};

// Strict colour budget: gray carries all structure; teal is the single brand
// accent (total rate + applicable); amber means caution (uncertain + warnings);
// red means critical (ADD/CVD) only. Nothing else is tinted.
function addonStatus(addon: TariffAddon) {
  if (addon.excluded) return { dot: 'bg-gray-300', label: 'Excluded', text: 'text-gray-400' };
  if (addon.applicable === true) return { dot: 'bg-teal-500', label: 'Applicable', text: 'text-teal-700' };
  if (addon.applicable === false) return { dot: 'bg-gray-300', label: 'N/A', text: 'text-gray-400' };
  return { dot: 'bg-amber-500', label: 'Uncertain', text: 'text-amber-700' };
}

const flagIconColor = (severity: string) =>
  severity === 'critical' ? 'text-red-500' : severity === 'info' ? 'text-gray-400' : 'text-amber-500';

const FLAG_TITLE: Record<string, string> = {
  addcvd: 'ADD/CVD — potential scope',
  trq: 'Tariff-rate quota',
  value_required: 'Customs value required',
  staleness: 'Reference data freshness',
  scope: 'Scope note',
};

/** A note/warning. Every flag uses the same neutral surface; severity shows only
 *  through the icon colour, so the section never turns into blocks of colour. */
const FlagBanner: React.FC<{ flag: TariffFlag }> = ({ flag }) => (
  <div className="mb-2 flex items-start gap-2.5 px-3 py-2.5 bg-gray-50 rounded-lg">
    <Icon name="exclamation-triangle" className={`w-4 h-4 flex-shrink-0 mt-0.5 ${flagIconColor(flag.severity)}`} />
    <div className="text-sm leading-relaxed">
      <span className="font-semibold text-gray-900">{FLAG_TITLE[flag.kind] || 'Note'}: </span>
      <span className="text-gray-600">{flag.message}</span>
      {flag.kind === 'addcvd' && Array.isArray(flag.detail?.case_numbers) && flag.detail!.case_numbers.length > 0 && (
        <span className="block mt-1 text-xs text-gray-400 tabular-nums">
          Case(s): {flag.detail!.case_numbers.join(', ')}
        </span>
      )}
    </div>
  </div>
);

/** A single additional-tariff program. Status is a coloured dot + label; the
 *  rate, basis and metadata are always visible (no click to reveal). */
const AddonRow: React.FC<{ label: string; addon: TariffAddon }> = ({ label, addon }) => {
  const cfg = addonStatus(addon);
  const muted = addon.applicable === false || addon.excluded;
  const meta = [addon.as_of && `as of ${addon.as_of}`, addon.confidence && `${addon.confidence} confidence`]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="py-3 first:pt-0 last:pb-0">
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
        <span className="text-sm font-medium text-gray-900">{label}</span>
        <span className={`text-xs font-medium ${cfg.text}`}>{cfg.label}</span>
      </div>
      <div className="ml-4 mt-1 space-y-1">
        {addon.rate_display && (
          <p className={`text-sm leading-relaxed ${muted ? 'text-gray-400' : 'text-gray-600'}`}>
            {addon.rate_display}
          </p>
        )}
        {(addon.basis || addon.reason) && (
          <p className="text-sm text-gray-500 leading-relaxed">{addon.basis || addon.reason}</p>
        )}
        {meta && <p className="text-xs text-gray-400">{meta}</p>}
      </div>
    </div>
  );
};

const fmtPct = (n: number | null | undefined): string =>
  n === null || n === undefined ? '—' : `${Number(n).toFixed(Number(n) % 1 === 0 ? 0 : 2)}%`;

/** Detailed duty breakdown (rate grid, stacking, programs, warnings, disclaimer).
 *  Shared by both the standalone card and the embedded section. */
const BreakdownDetails: React.FC<{ data: TariffDeterminationData }> = ({ data }) => {
  const [showAllWarnings, setShowAllWarnings] = useState(false);
  const addons = data.addons || {};

  return (
    <>
      {/* Rate breakdown — gray for the inputs, teal for the headline total. */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <div className="text-xs text-gray-500 mb-1">Base duty (MFN)</div>
          <div className="text-base font-semibold text-gray-900">
            {data.base_mfn_rate?.rate_display || fmtPct(data.base_mfn_rate?.ad_valorem_pct ?? null)}
          </div>
        </div>
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <div className="text-xs text-gray-500 mb-1">Additional</div>
          <div className="text-base font-semibold text-gray-900">{fmtPct(data.total_additional_pct)}</div>
        </div>
        <div className="text-center p-3 bg-teal-50 rounded-lg">
          <div className="text-xs text-teal-700 mb-1">Total rate</div>
          <div className="text-base font-semibold text-teal-900">{fmtPct(data.estimated_total_duty_pct)}</div>
        </div>
      </div>

      {/* Stacking order */}
      {data.stacking_order && data.stacking_order.length > 1 && (
        <div className="mb-5 text-xs text-gray-500 leading-relaxed">
          <span className="font-medium text-gray-600">Stacking order:</span> {data.stacking_order.join(' → ')} (additive on customs value)
        </div>
      )}

      {/* Additional tariff programs — divided rows, no nested boxes. */}
      <div className="mb-5">
        <div className="text-sm font-semibold text-gray-900 mb-1">Additional tariff programs</div>
        <div className="divide-y divide-gray-100">
          {PROGRAM_ORDER.map((key) => {
            const addon = addons[key];
            if (!addon) return null;
            return <AddonRow key={key} label={PROGRAM_LABELS[key]} addon={addon} />;
          })}
        </div>
      </div>

      {/* Warnings */}
      {data.warnings && data.warnings.length > 0 && (
        <div className="mb-5">
          <div className="text-sm font-semibold text-gray-900 mb-2">Notes &amp; warnings</div>
          <ul className="space-y-2">
            {(showAllWarnings ? data.warnings : data.warnings.slice(0, 2)).map((warning, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600 leading-relaxed">
                <Icon name="exclamation-triangle" className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-500" />
                <span>{warning}</span>
              </li>
            ))}
          </ul>
          {data.warnings.length > 2 && (
            <button
              onClick={() => setShowAllWarnings(!showAllWarnings)}
              className="mt-2 flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-800 transition-colors"
            >
              <Icon name={showAllWarnings ? 'chevron-up' : 'chevron-down'} className="w-3.5 h-3.5" />
              {showAllWarnings ? 'Show less' : `Show ${data.warnings.length - 2} more`}
            </button>
          )}
        </div>
      )}

      {/* Disclaimer */}
      <p className="text-xs text-gray-400 leading-relaxed">
        {data.informational_only ||
          'Duty rates are informational only and must not influence classification choice.'}
      </p>
    </>
  );
};

/** Computed duty-owed amount (when a customs value is available) plus any
 *  excluded-program note. The "missing customs value" caveat is intentionally
 *  not repeated here — it is carried once by the value_required flag. */
const DutyOwedLine: React.FC<{ data: TariffDeterminationData }> = ({ data }) => {
  const excludedFromTotal = data.excluded_from_total || [];
  const hasOwed = data.duty_owed !== null && data.duty_owed !== undefined;

  if (!hasOwed && excludedFromTotal.length === 0) return null;

  return (
    <>
      {hasOwed && (
        <div className="text-xs text-gray-500 mt-1">
          ≈ USD {Number(data.duty_owed).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          {data.customs_value ? <span> on customs value USD {Number(data.customs_value).toLocaleString()}</span> : null}
          {data.duty_owed_resolvable === false && (
            <span className="text-gray-400"> (excludes unresolved per-unit portion)</span>
          )}
        </div>
      )}
      {excludedFromTotal.length > 0 && (
        <div className="text-xs text-gray-400 mt-1">
          Excludes {excludedFromTotal.join(', ')} (flagged separately).
        </div>
      )}
    </>
  );
};

const TariffDeterminationResult: React.FC<TariffDeterminationResultProps> = ({ data, embedded = false }) => {
  const [expanded, setExpanded] = useState(false);

  const flags = data.flags || [];
  const isWebSearch = data.path === 'web_search';
  const sortedFlags = [...flags].sort(
    (a, b) => (a.severity === 'critical' ? -1 : 0) - (b.severity === 'critical' ? -1 : 0)
  );
  const criticalFlags = sortedFlags.filter((f) => f.severity === 'critical');
  const otherFlags = sortedFlags.filter((f) => f.severity !== 'critical');

  /* ── Embedded: subordinate section with a one-line summary that expands ── */
  if (embedded) {
    return (
      <div className="mt-5 border-t border-gray-100 pt-4">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center justify-between w-full text-left group"
        >
          <span className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-semibold text-gray-900">Tariff determination</span>
            <span className="inline-flex items-center gap-1 text-xs text-gray-400">
              <span className={`w-1.5 h-1.5 rounded-full ${isWebSearch ? 'bg-amber-500' : 'bg-teal-500'}`} />
              {isWebSearch ? 'Web-search' : 'Reference'}
            </span>
          </span>
          <span className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-gray-400">
              Base {data.base_mfn_rate?.rate_display || fmtPct(data.base_mfn_rate?.ad_valorem_pct ?? null)}
              {' + '}Add’l {fmtPct(data.total_additional_pct)}
            </span>
            <span className="text-sm font-semibold text-gray-900">
              {fmtPct(data.estimated_total_duty_pct)}
              {data.has_specific_components && <span className="text-xs font-medium text-gray-500"> + per-unit</span>}
            </span>
            <Icon
              name="chevron-down"
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            />
          </span>
        </button>

        <DutyOwedLine data={data} />

        {/* Critical flags (ADD/CVD etc.) stay visible even when collapsed. */}
        <div className="mt-3">
          {criticalFlags.map((flag, i) => (
            <FlagBanner key={i} flag={flag} />
          ))}
        </div>

        {expanded && (
          <div className="mt-1">
            {otherFlags.map((flag, i) => (
              <FlagBanner key={i} flag={flag} />
            ))}
            <BreakdownDetails data={data} />
          </div>
        )}
      </div>
    );
  }

  /* ── Standalone card (fallback when there is no HTS result to nest under) ── */
  return (
    <div className="bg-white rounded-lg p-6 border border-gray-200 mt-3">
      <div className="mb-5 flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Tariff Determination</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            HTS {data.hts_code} · {data.origin_country}
            {data.as_of_date && <span> · rate as of {data.as_of_date}</span>}
          </p>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium bg-gray-50 text-gray-600 border-gray-200">
          <span className={`w-2 h-2 rounded-full ${isWebSearch ? 'bg-amber-500' : 'bg-teal-500'}`} />
          {isWebSearch ? 'Web-search check' : 'Reference lookup'}
        </div>
      </div>

      {sortedFlags.map((flag, i) => (
        <FlagBanner key={i} flag={flag} />
      ))}

      <div className="mb-5 p-4 bg-gray-50 rounded-lg">
        <div className="text-xs text-gray-500 mb-1">Total duty (estimated)</div>
        <div className="text-2xl font-bold text-gray-900">
          {fmtPct(data.estimated_total_duty_pct)}
          {data.has_specific_components && (
            <span className="text-sm font-medium text-gray-500"> + per-unit</span>
          )}
        </div>
        <DutyOwedLine data={data} />
      </div>

      <BreakdownDetails data={data} />
    </div>
  );
};

export default TariffDeterminationResult;
